import { useState, useEffect } from "react";
import InputField from "components/InputField";
import SelectField from "components/SelectField";
import Card from "components/Card";
import { fetchEmployeeOwnProfileAPI, fetchSpecificEmployeeAPI, getRemainingLeavesTypesInfo, postHRAddLeave } from "api/apiHelper";
import { EmployeeLeaveInfoType } from "types/leaves-types";
import { postRequestLeave } from "api/apiHelper";
import Button from "components/Button";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useNotifications } from 'context/NotificationContext';


export interface RequestLeaveType {
  leave_info_id: string;
  start_date: string;
  end_date: string;
  reason: string;
}

export interface LeaveOptionsType {
  value: string;
  label: string;
  remaining_leaves?: number;
  leave_type_description?: string;
}

interface RequestLeaveFormProps {
  setShowRequestLeave?: (show: boolean) => void;
  isHR?: boolean;
  selectedEmployeeId?: string;
}

const RequestLeaveForm = ({ setShowRequestLeave, isHR = false, selectedEmployeeId }: RequestLeaveFormProps) => {
  const [remainingLeaves, setRemainingLeaves] = useState<EmployeeLeaveInfoType[]>([]);
  const [employee, setEmployee] = useState<EmployeeType | null>(null);
  const [leaveOptions, setLeaveOptions] = useState<LeaveOptionsType[]>([]);
  const [workingDays, setWorkingDays] = useState<number>(0);
  const navigate = useNavigate();
  const { refreshNotifications } = useNotifications();


  const [formData, setFormData] = useState<RequestLeaveType>({
    leave_info_id: "",
    start_date: "",
    end_date: "",
    reason: ""
  });

  const calculateWorkingDays = (startDate: string, endDate: string): number => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (end < start) return 0;

    let workingDaysCount = 0;
    const currentDate = new Date(start);

    while (currentDate <= end) {
      const dayOfWeek = currentDate.getDay();
      // 0 = Sunday, 6 = Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDaysCount++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDaysCount;
  };

  // Consolidate duplicate leave types
  const consolidateLeaveData = (data: EmployeeLeaveInfoType[]): (EmployeeLeaveInfoType & { ids: number[] })[] => {
    if (!data || data.length === 0) return []

    const consolidated: { [key: string]: EmployeeLeaveInfoType & { ids: number[] } } = {}

    data.forEach((leaveInfo) => {
      const leaveType = leaveInfo.leave_type_description
      
      if (consolidated[leaveType]) {
        consolidated[leaveType] = {
          ...consolidated[leaveType],
          max_leaves_allowed: (consolidated[leaveType].max_leaves_allowed || 0) + (leaveInfo.max_leaves_allowed || 0),
          remaining_leaves: (consolidated[leaveType].remaining_leaves || 0) + (leaveInfo.remaining_leaves || 0),
          ids: [...consolidated[leaveType].ids, leaveInfo.id]
        }
      } else {
        consolidated[leaveType] = { 
          ...leaveInfo,
          ids: [leaveInfo.id]
        }
      }
    })

    return Object.values(consolidated)
  }

  const fetchEmployeeDetails = async () => {
    try {
      let response;
      if (isHR && selectedEmployeeId) {
        // Fetch the selected employee's profile when HR is adding leave
        response = await fetchSpecificEmployeeAPI(selectedEmployeeId);
      } else {
        // Fetch the logged-in user's own profile
        response = await fetchEmployeeOwnProfileAPI();
      }
      setEmployee(response.data);
    } catch (error) {
      console.log("Failed to fetch employee details:", error);
    }
  };

  useEffect(() => {
    fetchEmployeeDetails();
  }, [isHR, selectedEmployeeId]);

  const fetchLeaves = async () => {
    try {
      let response;
  
      if (isHR && selectedEmployeeId) {
        response = await getRemainingLeavesTypesInfo(selectedEmployeeId);
      } else {
        response = await getRemainingLeavesTypesInfo();
      }
      console.log("Remaining Leaves: ", response);
      
      setRemainingLeaves(response.data);
    } catch (error) {
      console.log("Failed to fetch leave info:", error);
    }
  };

  useEffect(() => {
    const consolidatedLeaves = consolidateLeaveData(remainingLeaves);
    
    const options = consolidatedLeaves
      .filter(leave => (leave.remaining_leaves || 0) > 0)
      .map(leave => ({
        value: leave.ids[0].toString(),
        label: `${leave.leave_type_description} (${leave.remaining_leaves || 0} remaining)`,
      }));
      
    options.unshift({ value: "", label: "Select a leave type" });
    setLeaveOptions(options);
  }, [remainingLeaves]);

  useEffect(() => {
    fetchLeaves();
  }, [isHR, selectedEmployeeId]);

  // Update working days when dates change
  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      const days = calculateWorkingDays(formData.start_date, formData.end_date);
      setWorkingDays(days);
    } else {
      setWorkingDays(0);
    }
  }, [formData.start_date, formData.end_date]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.leave_info_id) {
      toast.error("Please select a leave type");
      return;
    }
    
    if (!formData.start_date) {
      toast.error("Please select a start date");
      return;
    }
    
    if (!formData.end_date) {
      toast.error("Please select an end date");
      return;
    }
    
    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error("End date cannot be before start date");
      return;
    }

    if (workingDays === 0) {
      toast.error("Leave request must include at least one working day (weekends are excluded)");
      return;
    }
    
    if (!formData.reason.trim()) {
      toast.error("Please provide a reason for the leave");
      return;
    }

    const notification = toast.loading("Requesting Leave...");

    try {
      let response;

      if (isHR && selectedEmployeeId) {
        const hrPayload = {
          ...formData,
          employee_id: Number(selectedEmployeeId),
        } as RequestLeaveType & { employee_id: number };
  
        response = await postHRAddLeave(hrPayload);
      } else {
        response = await postRequestLeave(formData);
      }
      console.log("Request Leave Response: ", response);
      
      if (response.data?.distribution_info) {
        const distributionSummary = response.data.distribution_info
          .map((dist: any) => `${dist.days_consumed} days from period ending ${dist.period_end}`)
          .join(', ');
        
        toast.update(notification, {
          render: `Leave requested successfully! Distribution: ${distributionSummary}`,
          type: "success",
          isLoading: false,
          autoClose: 7000,
        });
      } else {
        toast.update(notification, {
          render: "Leave requested successfully",
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
      }
      
      handleReset();
      fetchLeaves();
      refreshNotifications();
    } catch (error: any) {
      console.error("Leave request error:", error);
      
      let errorMessage = "Failed to request leave";
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.update(notification, {
        render: errorMessage,
        type: "error",
        isLoading: false,
        autoClose: 7000,
      });
    }
  };

  const handleReset = () => {
    setFormData({
      leave_info_id: "",
      start_date: "",
      end_date: "",
      reason: ""
    });
    setWorkingDays(0);
  };

  return (
    <Card extra={"w-full h-full pb-10 p-4"}>
      <div className="mb-8 mt-2 w-full flex justify-between items-center">
        <div>
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Request Leave
          </h4>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Submit your leave request by filling out the form below
          </p>
        </div>
        
        {setShowRequestLeave && (
          <button
            className="bg-brand-500 flex gap-2 justify-center items-center uppercase hover:bg-brand-600 dark:bg-brand-400 dark:hover:bg-brand-500 rounded-lg px-4 py-3 text-[0.8rem] font-bold text-white"
            onClick={() => setShowRequestLeave(false)}
          >
            <i className="fas fa-info-circle text-[0.8rem] leading-none"></i>
            <span className="leading-none">Leaves Info</span>
          </button>
        )}
      </div>

      <form className="flex w-full flex-col gap-4" onSubmit={handleSubmit} onReset={handleReset}>
        <SelectField 
          id="leave_info_id"
          label="Leave Type"
          options={leaveOptions}
          onChange={handleChange}
          value={formData.leave_info_id}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            id="start_date"
            label="Start Date"
            type="date"
            placeholder="Enter start date"
            extra="w-full"
            variant="outlined"
            onChange={handleChange}
            value={formData.start_date}
            min={isHR? undefined : new Date().toISOString().split('T')[0]}
          />
          <InputField
            id="end_date"
            label="End Date"
            type="date"
            placeholder="Enter end date"
            extra="w-full"
            variant="outlined"
            onChange={handleChange}
            value={formData.end_date}
            min={isHR ? (formData.start_date || undefined) : (formData.start_date || new Date().toISOString().split('T')[0])}
          />
        </div>

        {/* Show working days calculation */}
        {formData.start_date && formData.end_date && (
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <span className="font-medium">Working Days:</span> {workingDays} {workingDays === 1 ? 'day' : 'days'}
            </p>
            <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
              Weekends (Saturday & Sunday) are automatically excluded
            </p>
          </div>
        )}
        
        <InputField
          id="reason"
          label="Reason"
          type="text"
          placeholder="Enter reason for leave request"
          extra="w-full"
          variant="outlined"
          onChange={handleChange}
          value={formData.reason}
        />
        
        <InputField
          id="reports_to"
          label="Reports To"
          type="text"
          placeholder="Manager's name"
          extra="w-full"
          variant="outlined"
          onChange={handleChange}
          value={employee?.manager_name || "Not Assigned"}
          disabled={true}
        />
        
        <div className="flex justify-end gap-2 pt-4">
          <Button type="reset" varient="secondary" className="w-max">Cancel</Button>
          <Button type="submit" varient="primary" className="w-max">Submit Request</Button>
        </div>
      </form>
    </Card>
  );
};

export default RequestLeaveForm;